import React, { useEffect, useState, useRef, useCallback } from "react";
import { api, resolveImage } from "../../lib/api";
import { Reorder } from "framer-motion";
import { Plus, Pencil, Trash2, Upload, Loader2, X, GripVertical, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const empty = { image_url: "", caption: "" };

export default function DonationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reorderSaving, setReorderSaving] = useState(false);
  const fileInputRef = useRef(null);
  const reorderTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/donations");
      setItems(data || []);
    } catch {
      toast.error("Failed to load donations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const persistOrder = useCallback((next) => {
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(async () => {
      setReorderSaving(true);
      try {
        await api.put("/admin/donations/reorder", { photo_ids: next.map((i) => i.id) });
      } catch {
        toast.error("Could not save new order");
      } finally {
        setReorderSaving(false);
      }
    }, 600);
  }, []);

  const handleReorder = (next) => {
    setItems(next);
    persistOrder(next);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, image_url: data.public_url }));
      toast.success("Photo uploaded");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ image_url: p.image_url, caption: p.caption || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.image_url) {
      toast.error("Please upload an image first");
      return;
    }
    setSaving(true);
    try {
      const payload = { image_url: form.image_url, caption: form.caption };
      if (editing) {
        await api.put(`/admin/donations/${editing.id}`, payload);
        toast.success("Photo updated");
      } else {
        await api.post("/admin/donations", payload);
        toast.success("Photo added to gallery");
      }
      setOpen(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!confirm(`Remove this photo from the gallery?\n\n${p.caption || "(no caption)"}\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/admin/donations/${p.id}`);
      toast.success("Photo removed");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="donations-tab">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
        <div>
          <p className="font-script text-2xl" style={{ color: "var(--text-accent)" }}>
            donations of the past
          </p>
          <h1 className="font-display text-4xl" style={{ color: "var(--text-primary)" }}>
            Gallery
          </h1>
          <p className="text-xs mt-1 flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
            <GripVertical size={12} />
            Drag photos to reorder · they show in this order on the homepage
            {reorderSaving && <span className="font-script">· saving order…</span>}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="btn-sage inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
          data-testid="add-donation-button"
        >
          <Plus size={16} />
          Add Photo
        </button>
      </header>

      {loading ? (
        <p className="font-script text-xl" style={{ color: "var(--text-secondary)" }}>loading…</p>
      ) : items.length === 0 ? (
        <div
          className="bg-white rounded-2xl p-10 text-center"
          style={{ border: "1px dashed var(--border-soft)" }}
          data-testid="donations-empty-state"
        >
          <ImagePlus size={36} className="mx-auto" style={{ color: "var(--brand-peach)" }} />
          <p className="font-script text-2xl mt-3" style={{ color: "var(--text-accent)" }}>
            your gallery awaits
          </p>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Add photos from past donation events to share with your community.
          </p>
        </div>
      ) : (
        <Reorder.Group
          axis="y"
          values={items}
          onReorder={handleReorder}
          className="space-y-3 max-w-3xl"
          data-testid="donations-reorder-group"
        >
          {items.map((p) => (
            <Reorder.Item
              key={p.id}
              value={p}
              className="bg-white rounded-2xl overflow-hidden flex items-stretch"
              style={{ border: "1px solid var(--border-soft)" }}
              data-testid={`admin-donation-${p.id}`}
            >
              <div
                className="flex items-center px-2 cursor-grab active:cursor-grabbing select-none"
                style={{ color: "var(--text-secondary)" }}
                aria-label="Drag to reorder"
              >
                <GripVertical size={18} />
              </div>
              <div className="w-32 h-24 sm:w-40 sm:h-28 flex-shrink-0 bg-[var(--bg-secondary)]">
                <img src={resolveImage(p.image_url)} alt={p.caption || "donation"} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 sm:p-4 flex-1 flex flex-col min-w-0">
                <p className="text-sm leading-relaxed flex-1" style={{ color: "var(--text-primary)" }}>
                  {p.caption || <span style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>No caption</span>}
                </p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => openEdit(p)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)" }}
                    data-testid={`edit-donation-${p.id}`}
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    onClick={() => remove(p)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ border: "1px solid var(--border-soft)", color: "var(--brand-orange)" }}
                    data-testid={`delete-donation-${p.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg max-h-[90vh] overflow-y-auto"
          style={{ backgroundColor: "var(--bg-primary)" }}
          data-testid="donation-editor-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl" style={{ color: "var(--text-primary)" }}>
              {editing ? "Edit Photo" : "Add Photo"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-semibold block mb-1.5">Photo</label>
              {form.image_url ? (
                <div className="relative">
                  <img
                    src={resolveImage(form.image_url)}
                    alt="preview"
                    className="w-full h-56 object-cover rounded-xl"
                    style={{ border: "1px solid var(--border-soft)" }}
                  />
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, image_url: "" }))}
                    className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow"
                    aria-label="Remove photo"
                    data-testid="donation-clear-image"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full h-56 rounded-xl stitched flex flex-col items-center justify-center text-sm gap-2 disabled:opacity-60"
                  style={{ color: "var(--text-secondary)" }}
                  data-testid="donation-upload-button"
                >
                  {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                  {uploading ? "Uploading…" : "Click to upload a photo (jpg/png, max 5MB)"}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                hidden
                onChange={onUpload}
                data-testid="donation-file-input"
              />
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">
                Caption <span className="font-normal text-xs" style={{ color: "var(--text-secondary)" }}>(optional)</span>
              </label>
              <textarea
                rows={3}
                maxLength={200}
                value={form.caption}
                onChange={(e) => setForm((p) => ({ ...p, caption: e.target.value }))}
                className="w-full px-4 py-2 rounded-xl outline-none resize-none"
                style={{ backgroundColor: "#fff", border: "1px solid var(--border-soft)" }}
                placeholder="e.g. Christmas 2024 — Children's Hospital"
                data-testid="donation-caption-input"
              />
              <div className="text-right text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                {form.caption.length} / 200
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 py-2.5 rounded-full text-sm font-semibold"
                style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)" }}
                data-testid="cancel-donation-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || uploading || !form.image_url}
                className="btn-sage flex-1 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60"
                data-testid="save-donation-button"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Add to gallery"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
